/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { GhostCharacteristics } from '../model/GhostCharacteristics';
import { Dispatch } from 'redux';
import {
  setAuthKey,
  setBatteryLevel,
  setCurrentProfile,
  connectFailed,
  connectStart,
  setTemperatures,
  setStatus,
} from '../store/reducers/bleReducer';
import { RootState } from '../store';
import { randomBytes } from 'crypto';
import { TaskQueue } from 'typescript-task-queue';

const pause = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(() => resolve(), ms));
};

class GhostBle {
  dispatch?: Dispatch;
  getState?: () => RootState;
  device?: BluetoothDevice;
  server?: BluetoothRemoteGATTServer;
  service?: BluetoothRemoteGATTService;
  batteryService?: BluetoothRemoteGATTService;
  authCharacteristic?: BluetoothRemoteGATTCharacteristic;
  batteryCharacteristic?: BluetoothRemoteGATTCharacteristic;
  currentProfileCharacteristic?: BluetoothRemoteGATTCharacteristic;
  temperatureCharacteristic?: BluetoothRemoteGATTCharacteristic;
  statusCharacteristic?: BluetoothRemoteGATTCharacteristic;

  private queue: TaskQueue = new TaskQueue({ autorun: true, stoppable: false });

  initialize = (dispatch: Dispatch, getState: () => RootState): void => {
    this.dispatch = dispatch;
    this.getState = getState;
  };

  private initializeBle = async (allDevices: boolean, isPair = false): Promise<void> => {
    if (!this.device || !this.device.gatt) {
      if (allDevices) {
        this.device = await navigator.bluetooth.requestDevice({
          optionalServices: [GhostCharacteristics.serviceUuid, GhostCharacteristics.batteryServiceUuid],
          acceptAllDevices: true,
        });
      } else {
        const nameFilters = [];
        const deviceName = localStorage.getItem('ghostmv1.name');
        if (deviceName) {
          nameFilters.push({ name: deviceName });
          nameFilters.push({ name: deviceName.toLowerCase() });
          nameFilters.push({ name: deviceName.toUpperCase() });
        }
        const serviceFilters = isPair
          ? [
              {
                services: [GhostCharacteristics.pairingUuid],
              },
            ]
          : [
              {
                services: [GhostCharacteristics.deviceUuid],
              },
            ];

        this.device = await navigator.bluetooth.requestDevice({
          filters: [...nameFilters, ...serviceFilters],
          optionalServices: [GhostCharacteristics.serviceUuid, GhostCharacteristics.batteryServiceUuid],
          // acceptAllDevices: true,
        });
      }

      if (!this.device || !this.device.gatt) {
        throw new Error('Failed to retrieve GhostBLE device.');
      }
      if (this.device.name) {
        localStorage.setItem('ghostmv1.name', this.device.name);
      }

      this.device.ongattserverdisconnected = async (): Promise<void> => {
        this.dispatch!(connectFailed(`Failed to connect to Ghost MV1.  Retrying ...`));
      };
    }

    this.dispatch!(connectStart());

    this.server = await this.device.gatt.connect();
    if (!this.server) {
      throw new Error('Failed to retrieve BLE server.');
    }

    // let count = 0;
    // while (!this.server.connected && count++ <= 50) {
    //   console.log('Waiting for connection to server ...');
    //   await pause(100);
    // }
    await pause(1000);
    this.service = await this.server.getPrimaryService(GhostCharacteristics.serviceUuid);
  };

  pair = async (allDevices: boolean): Promise<void> => {
    if (!this.server || !this.server.connected) {
      await this.initializeBle(allDevices, true);
    }

    await pause(1000);
    this.authCharacteristic = await this.service!.getCharacteristic(GhostCharacteristics.pairingKey.uuid);

    await new Promise((resolve, reject) => {
      try {
        let resolved = false;
        this.authCharacteristic!.addEventListener(
          'characteristicvaluechanged',
          async (): Promise<void> => {
            try {
              if (!resolved) {
                resolved = true;
                await this.authCharacteristic!.stopNotifications();
                const rawValue = this.authCharacteristic!.value;
                if (rawValue) {
                  const value = hexToString(rawValue);
                  localStorage.setItem('ghostmv1.auth', value);
                  this.dispatch!(setAuthKey(value));
                  await pause(250);
                  await this.authCharacteristic!.writeValue(stringToHex(value));
                }

                resolve();
              }
            } catch (e) {
              reject(e);
            }
          },
        );
        this.authCharacteristic!.startNotifications().then(() => {
          this.authCharacteristic!.readValue();
        });
      } catch (exc) {
        reject(exc);
      }
    });
    await this.connect(allDevices);
  };

  connect = async (allDevices: boolean): Promise<void> => {
    const key = localStorage.getItem('ghostmv1.auth');
    if (!key) {
      throw new Error('No paired device.');
    }

    if (!this.server || !this.server.connected) {
      await this.initializeBle(allDevices);
    }

    await pause(1000);
    this.authCharacteristic = await this.service!.getCharacteristic(GhostCharacteristics.pairingKey.uuid);
    await pause(250);
    if (key && key !== '' && key.length === 12) {
      await this.authCharacteristic!.writeValue(stringToHex(key));
    }
    await pause(250);

    await new Promise((resolve, reject) => {
      try {
        this.authCharacteristic!.addEventListener(
          'characteristicvaluechanged',
          async (): Promise<void> => {
            try {
              const rawValue = this.authCharacteristic!.value;
              if (rawValue) {
                const value = hexToString(rawValue);
                if (value !== this.getState!().ble.authKey) {
                  localStorage.setItem('ghostmv1.auth', value);
                  this.dispatch!(setAuthKey(value));
                }
                this.authCharacteristic!.stopNotifications();
                resolve();
              }
            } catch (e) {
              reject(e);
            }
          },
        );
        this.authCharacteristic!.startNotifications().then(() => {
          pause(100)
            .then(() => {
              this.authCharacteristic!.readValue();
            })
            .catch(reason => {
              reject(reason);
            });
        });
      } catch (exc) {
        reject(exc);
      }
    });

    await pause(100);
    this.statusCharacteristic = await this.service!.getCharacteristic(GhostCharacteristics.status.uuid);
    await new Promise((resolve, reject) => {
      try {
        this.statusCharacteristic!.addEventListener(
          'characteristicvaluechanged',
          async (): Promise<void> => {
            if (this.getState!().ble.updating) {
              return;
            }

            this.queue.enqueue(async () => {
              try {
                const rawValue = this.statusCharacteristic!.value;
                if (rawValue) {
                  const statusValue = await hexToNumberValue(rawValue);
                  if (statusValue !== this.getState!().ble.status) {
                    await this.dispatch!(setStatus(statusValue));
                  }
                  resolve();
                }
              } catch (e) {
                reject(e);
              }
            });
          },
        );
        this.statusCharacteristic!.startNotifications().then(() => {
          this.statusCharacteristic!.readValue();
        });
      } catch (exc) {
        reject(exc);
      }
    });

    await pause(100);
    this.currentProfileCharacteristic = await this.service!.getCharacteristic(GhostCharacteristics.currentProfile.uuid);
    await new Promise((resolve, reject) => {
      try {
        this.currentProfileCharacteristic!.addEventListener(
          'characteristicvaluechanged',
          async (): Promise<void> => {
            if (this.getState!().ble.updating) {
              return;
            }

            this.queue.enqueue(async () => {
              try {
                const rawValue = this.currentProfileCharacteristic!.value;
                if (rawValue) {
                  const profileValue = hexToNumberValue(rawValue);
                  if (profileValue !== this.getState!().ble.profile) {
                    this.dispatch!(setCurrentProfile(profileValue));
                  }
                  resolve();
                }
              } catch (e) {
                reject(e);
              }
            });
          },
        );
        this.currentProfileCharacteristic!.startNotifications().then(() => {
          this.currentProfileCharacteristic!.readValue();
        });
      } catch (e) {
        reject(e);
      }
    });

    await pause(100);
    this.temperatureCharacteristic = await this.service!.getCharacteristic(GhostCharacteristics.temperatures.uuid);
    await new Promise((resolve, reject) => {
      try {
        this.temperatureCharacteristic!.addEventListener(
          'characteristicvaluechanged',
          async (): Promise<void> => {
            if (this.getState!().ble.updating) {
              return;
            }
            this.queue.enqueue(async () => {
              try {
                const rawValue = this.temperatureCharacteristic!.value;
                if (rawValue) {
                  const temperatureValue = readTemperatures(rawValue);
                  const oldTempValue = this.getState!().ble.temperatures;
                  if (
                    temperatureValue.length !== oldTempValue.length ||
                    temperatureValue.filter((value, index) => value !== oldTempValue[index]).length > 0
                  ) {
                    await this.dispatch!(setTemperatures(temperatureValue));
                  }
                  resolve();
                }
              } catch (e) {
                reject(e);
              }
            });
          },
        );
        this.temperatureCharacteristic!.startNotifications().then(() => {
          this.temperatureCharacteristic!.readValue();
        });
      } catch (exc) {
        reject(exc);
      }
    });

    await pause(100);
    this.batteryService = await this.server!.getPrimaryService(GhostCharacteristics.batteryServiceUuid);
    await pause(100);
    this.batteryCharacteristic = await this.batteryService.getCharacteristic(GhostCharacteristics.batteryLevelUuid);
    await pause(100);
    const rawBatteryValue = await this.batteryCharacteristic.readValue();
    const batteryValue = hexToNumberValue(rawBatteryValue);
    await this.dispatch!(setBatteryLevel(batteryValue));
  };

  disconnect = async (): Promise<void> => {
    if (this.server) {
      try {
        await this.pauseNotifications();
      } catch {
        // ignore
      }

      if (this.authCharacteristic) {
        await new Promise(resolve => {
          try {
            this.authCharacteristic!.removeEventListener('characteristicvaluechanged', () => {
              // ignore
            });
            resolve();
          } catch {
            resolve();
          }
        });
      }
      if (this.statusCharacteristic) {
        await new Promise(resolve => {
          try {
            this.statusCharacteristic!.removeEventListener('characteristicvaluechanged', () => {
              // ignore
            });
            resolve();
          } catch {
            resolve();
          }
        });
      }
      if (this.currentProfileCharacteristic) {
        await new Promise(resolve => {
          try {
            this.currentProfileCharacteristic!.removeEventListener('characteristicvaluechanged', () => {
              // ignore
            });
            resolve();
          } catch {
            resolve();
          }
        });
      }
      if (this.temperatureCharacteristic) {
        await new Promise(resolve => {
          try {
            this.temperatureCharacteristic!.removeEventListener('characteristicvaluechanged', () => {
              // ignore
            });
            resolve();
          } catch {
            resolve();
          }
        });
      }
      if (this.server && this.server.connected) {
        try {
          this.server.disconnect();
        } catch {
          // ignore
        }
      }
    }
    delete this.server;
  };

  updateCurrentProfile = async (profile: number): Promise<number | void> => {
    if (!this.currentProfileCharacteristic) {
      return;
    }

    const s = profile.toString(16).padStart(2, '0');
    const hexValue = stringToHex(s);

    return new Promise(resolve => {
      this.queue.enqueue(async () => {
        for (let i = 0; i < 5; i++) {
          try {
            await this.currentProfileCharacteristic!.writeValue(hexValue);
            await pause(250);
            resolve(profile);
            return;
          } catch (e) {
            console.log(`Error updating profile, trying again: ${e}`);
            await pause(250);
          }
        }
        console.log('failed to write profile');
        resolve(this.getState!().ble.profile);
      });
    });
  };

  updateTemperature = async (value: number[]): Promise<void> => {
    if (!this.temperatureCharacteristic) {
      return;
    }

    if (value.length < 6) {
      console.log('Tried to write invalid temperature values.');
      return;
    }

    if (value.filter(n => n !== 0).length === 0) {
      console.log('Tried to write all zero temperature values.');
      return;
    }

    return new Promise(resolve => {
      this.queue.enqueue(async () => {
        for (let i = 0; i < 5; i++) {
          try {
            await writeTemperatures(this.temperatureCharacteristic!, value);
            await pause(250);
            resolve();
            return;
          } catch (e) {
            console.log(`Error writing temperatures, trying again: ${e}`);
            await pause(250);
          }
        }
        console.log('Failed to write temperatures.');
        resolve();
      });
    });
  };

  lock = async (): Promise<boolean | void> => {
    if (!this.statusCharacteristic) {
      return;
    }

    // if (this.getState!().ble.updating) {
    const status = this.getState!().ble.status | 0x1;
    const buffer = new ArrayBuffer(1);
    const dv = new DataView(buffer);
    dv.setUint8(0, status);
    return new Promise(resolve => {
      this.queue.enqueue(async () => {
        for (let i = 0; i < 5; i++) {
          try {
            await this.statusCharacteristic!.writeValue(dv);
            await pause(250);
            resolve(true);
            return;
          } catch (e) {
            console.log(`Error locking vaporizer, trying again: ${e}`);
            await pause(250);
          }
        }
        console.log('Failed to lock vaporizer.');
        resolve(false);
      });
    });
  };

  unlock = async (): Promise<boolean | void> => {
    if (!this.statusCharacteristic) {
      return;
    }

    const status = this.getState!().ble.status & 0xfe;
    const buffer = new ArrayBuffer(1);
    const dv = new DataView(buffer);
    dv.setUint8(0, status);
    return new Promise(resolve => {
      this.queue.enqueue(async () => {
        for (let i = 0; i < 5; i++) {
          try {
            await this.statusCharacteristic!.writeValue(dv);
            await pause(250);
            resolve(true);
            return;
          } catch (e) {
            console.log(`Error unlocking vaporizer, trying again: ${e}`);
            await pause(250);
          }
        }
        console.log('Failed to unlock vaporizer.');
        resolve(false);
      });
    });
  };

  pauseNotifications = async (): Promise<void> => {
    await this.statusCharacteristic!.stopNotifications();
    await this.currentProfileCharacteristic!.stopNotifications();
    await this.temperatureCharacteristic!.stopNotifications();
  };

  startNotifications = async (): Promise<void> => {
    await this.statusCharacteristic!.startNotifications();
    await this.currentProfileCharacteristic!.startNotifications();
    await this.temperatureCharacteristic!.startNotifications();
    await pause(100);
  };
}

export const stringToHex = (value: string): DataView => {
  const buffer = new ArrayBuffer(6);
  const dv = new DataView(buffer);
  for (let i = 0; i < 6; i++) {
    dv.setUint8(i, parseInt(value.substr(i * 2, 2), 16));
  }
  return dv;
};

export const hexToString = (rawValue: DataView, bits = 8): string => {
  let s = '';
  if (rawValue && rawValue.byteLength > 0) {
    if (bits === 8) {
      for (let i = 0; i < rawValue.byteLength; i++) {
        s += rawValue.getUint8(i).toString(16);
      }
    } else if (bits === 16) {
      for (let i = 0; i < rawValue.byteLength; i += 2) {
        s += rawValue
          .getUint16(i, false)
          .toString(16)
          .padStart(4, '0');
      }
    }
  }
  return s;
};

export const hexToNumberValue = (rawValue: DataView): number => {
  if (rawValue.byteLength === 1) {
    return rawValue.getUint8(0);
  } else if (rawValue.byteLength === 2) {
    return rawValue.getUint16(0, true);
  }
  return 0;
};

export const readTemperatures = (rawValue: DataView): number[] => {
  const values = [];
  for (let i = 0; i < rawValue.byteLength; i += 2) {
    values.push(rawValue.getUint16(i, false));
  }
  return values;
};

export const writeTemperatures = async (
  characteristic: BluetoothRemoteGATTCharacteristic,
  value: number[],
): Promise<void> => {
  if (value.length < 6) {
    return;
  }

  const buffer = new ArrayBuffer(12);
  const dv = new DataView(buffer);
  for (let i = 0; i < 6; i++) {
    let currentValue = value[i];
    switch (i) {
      case 0: {
        currentValue = 170;
        break;
      }
      case 1: {
        currentValue = 185;
        break;
      }
      case 2: {
        currentValue = 200;
        break;
      }
      case 5: {
        currentValue = 220;
        break;
      }
    }
    dv.setUint16(i * 2, currentValue, false);
  }

  //alert(hexToString(dv, 16));
  await characteristic.writeValue(dv);
};

export const generateRandomKey = (): DataView => {
  const key = randomBytes(6);
  const buffer = new ArrayBuffer(6);
  const dv = new DataView(buffer);
  for (let i = 0; i < key.byteLength; i++) {
    dv.setUint8(i, key.readUInt8(i));
  }
  return dv;
};

const ghostBle = new GhostBle();
export { ghostBle as GhostBle };
